import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Sparkles, Trash2, Plus } from "lucide-react";

const CONTENT_TYPES = ["news", "evergreen_guide", "cost_guide", "product_review"];
const PILLARS = ["roofing", "plumbing", "hvac", "electrical", "water-damage", "bathroom-kitchen-remodeling", "smart-home", "weather", "recalls", "regulation", "real-estate", "home-retail"];
const CATEGORIES = ["news", "home-improvement", "repairs-maintenance", "home-costs", "smart-home", "homeowner-guides", "outdoors"];

const emptyForm = {
  slug: "", title: "", meta_title: "", meta_description: "", dek: "", content_type: "evergreen_guide",
  category_slug: "homeowner-guides", pillar: "roofing", tags: "", author_name: "HouseTalk Editorial Team",
  author_role: "Editorial Team", hero_image_url: "", hero_image_alt: "", body_html: "", read_time_minutes: 6,
  featured: false, breaking: false, status: "published", key_takeaways: "", cost_national_average: "", cost_range_low: "", cost_range_high: "", market: "US",
  ai_generated: false, ai_provider: "",
};
export default function AdminArticleEditor() {
  const { slug: slugParam } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = Boolean(slugParam);
  const [form, setForm] = useState(emptyForm);
  const [faq, setFaq] = useState([]);
  const [references, setReferences] = useState([]);
  const [aiTopic, setAiTopic] = useState("");
  const [aiProvider, setAiProvider] = useState("anthropic");
  const [briefId, setBriefId] = useState("");
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [markets, setMarkets] = useState([]);

  useEffect(() => { api.get("/markets").then((res) => setMarkets(res.data)); }, []);

  useEffect(() => {
    const prefill = location.state;
    if (!isEdit && prefill?.prefillTopic) {
      setAiTopic(prefill.prefillTopic);
      setForm((f) => ({
        ...f,
        content_type: prefill.prefillContentType || f.content_type,
        pillar: prefill.prefillPillar || f.pillar,
        market: prefill.prefillMarket || f.market,
      }));
      if (prefill.prefillBriefId) {
        setBriefId(prefill.prefillBriefId);
        toast.info("Brief-informed topic pre-filled — click Generate Draft to write it up");
      } else {
        toast.info("Topic pre-filled — click Generate Draft to write it up");
      }
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isEdit) {
      api.get("/admin/articles").then((res) => {
        const a = res.data.items.find((x) => x.slug === slugParam);
        if (a) {
          setForm({
            ...emptyForm, ...a, tags: (a.tags || []).join(", "), key_takeaways: (a.key_takeaways || []).join("\n"),
            cost_national_average: a.cost_summary?.national_average || "", cost_range_low: a.cost_summary?.range_low || "", cost_range_high: a.cost_summary?.range_high || "",
          });
          setFaq(a.faq || []);
          setReferences(a.references || []);
        }
      });
    }
  }, [slugParam, isEdit]);

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target ? e.target.value : e }));

  const generateWithAI = async () => {
    if (!aiTopic) { toast.error("Enter a topic first"); return; }
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/articles/ai-generate", { topic: aiTopic, content_type: form.content_type, pillar: form.pillar, provider: aiProvider, brief_id: briefId });
      setForm((f) => ({
        ...f,
        title: data.title || aiTopic,
        meta_title: data.meta_title || "",
        meta_description: data.meta_description || "",
        dek: data.dek || "",
        body_html: data.body_html || "",
        read_time_minutes: data.read_time_minutes || 6,
        tags: (data.tags || []).join(", "),
        key_takeaways: (data.key_takeaways || []).join("\n"),
        slug: f.slug || aiTopic.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, ""),
        cost_national_average: data.cost_summary?.national_average || f.cost_national_average,
        cost_range_low: data.cost_summary?.range_low || f.cost_range_low,
        cost_range_high: data.cost_summary?.range_high || f.cost_range_high,
        ai_generated: true,
        ai_provider: aiProvider,
      }));
      setFaq(data.faq || []);
      setReferences(data.references || []);
      if (briefId) {
        api.post(`/admin/newsroom/briefs/${briefId}/mark-drafted`).catch(() => {});
      }
      if (data.regulated_topic_warning) {
        toast.warning(data.regulated_topic_note || "This topic touches safety/financial/regulatory claims - verify against Tier 1 sources before publishing.", { duration: 8000 });
      }
      toast.success("Draft generated! Review and edit before publishing.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const payload = {
      ...form,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      key_takeaways: form.key_takeaways.split("\n").map((t) => t.trim()).filter(Boolean),
      faq,
      references,
      read_time_minutes: Number(form.read_time_minutes) || 5,
      cost_summary: form.content_type === "cost_guide" ? {
        national_average: Number(form.cost_national_average) || 0,
        range_low: Number(form.cost_range_low) || 0,
        range_high: Number(form.cost_range_high) || 0,
      } : null,
    };
    try {
      if (isEdit) {
        await api.put(`/admin/articles/${slugParam}`, payload);
        toast.success("Article updated");
      } else {
        await api.post("/admin/articles", payload);
        toast.success("Article created");
      }
      navigate("/admin/articles");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-article-editor-page" className="max-w-4xl">
      <h1 className="text-2xl font-serif-heading font-bold text-navy mb-6">{isEdit ? "Edit Article" : "New Article"}</h1>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 mb-6" data-testid="ai-generate-panel">
        <h2 className="font-semibold text-navy mb-3 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-blue-600" /> Generate Draft with AI
          {briefId && <span className="text-xs font-normal bg-blue-100 text-blue-700 px-2 py-0.5 rounded" data-testid="brief-informed-badge">Using editorial brief</span>}
        </h2>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input placeholder="Topic, e.g. 'How to Winterize Your Pipes'" value={aiTopic} onChange={(e) => setAiTopic(e.target.value)} data-testid="ai-topic-input" />
          <Select value={aiProvider} onValueChange={setAiProvider}>
            <SelectTrigger className="w-full sm:w-40" data-testid="ai-provider-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="anthropic">Claude</SelectItem><SelectItem value="openai">GPT</SelectItem></SelectContent>
          </Select>
          <Button onClick={generateWithAI} disabled={generating} data-testid="ai-generate-button" className="bg-blue-600 hover:bg-blue-700 flex-shrink-0">
            {generating ? "Generating..." : "Generate"}
          </Button>
        </div>
      </div>

      <div className="space-y-4 bg-white border border-slate-200 rounded-lg p-6">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Content Type">
            <Select value={form.content_type} onValueChange={(v) => setForm((f) => ({ ...f, content_type: v }))}>
              <SelectTrigger data-testid="article-content-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{CONTENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Category">
            <Select value={form.category_slug} onValueChange={(v) => setForm((f) => ({ ...f, category_slug: v }))}>
              <SelectTrigger data-testid="article-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Pillar">
            <Select value={form.pillar} onValueChange={(v) => setForm((f) => ({ ...f, pillar: v }))}>
              <SelectTrigger data-testid="article-pillar-select"><SelectValue /></SelectTrigger>
              <SelectContent>{PILLARS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Market">
            <Select value={form.market} onValueChange={(v) => setForm((f) => ({ ...f, market: v }))}>
              <SelectTrigger data-testid="article-market-select"><SelectValue /></SelectTrigger>
              <SelectContent>{markets.map((m) => <SelectItem key={m.code} value={m.code}>{m.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        </div>
        <Field label="Title"><Input value={form.title} onChange={update("title")} data-testid="article-title-input" /></Field>
        <Field label="Slug (URL)"><Input value={form.slug} onChange={update("slug")} data-testid="article-slug-input" /></Field>
        <Field label="Dek (subheading)"><Textarea value={form.dek} onChange={update("dek")} rows={2} data-testid="article-dek-input" /></Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Meta Title"><Input value={form.meta_title} onChange={update("meta_title")} data-testid="article-meta-title-input" /></Field>
          <Field label="Meta Description"><Input value={form.meta_description} onChange={update("meta_description")} data-testid="article-meta-description-input" /></Field>
        </div>
        <Field label="Hero Image URL"><Input value={form.hero_image_url} onChange={update("hero_image_url")} data-testid="article-hero-image-input" /></Field>
        <Field label="Body HTML"><Textarea value={form.body_html} onChange={update("body_html")} rows={12} className="font-mono text-sm" data-testid="article-body-textarea" /></Field>
        <Field label="Key Takeaways (one per line)"><Textarea value={form.key_takeaways} onChange={update("key_takeaways")} rows={3} data-testid="article-key-takeaways-textarea" /></Field>
        <Field label="Tags (comma separated)"><Input value={form.tags} onChange={update("tags")} data-testid="article-tags-input" /></Field>

        {form.content_type === "cost_guide" && (
          <div className="grid grid-cols-3 gap-4 bg-navy-50 p-4 rounded-lg" data-testid="cost-summary-fields">
            <Field label="National Average ($)"><Input type="number" value={form.cost_national_average} onChange={update("cost_national_average")} /></Field>
            <Field label="Range Low ($)"><Input type="number" value={form.cost_range_low} onChange={update("cost_range_low")} /></Field>
            <Field label="Range High ($)"><Input type="number" value={form.cost_range_high} onChange={update("cost_range_high")} /></Field>
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-navy">FAQ</label>
            <Button type="button" variant="outline" size="sm" onClick={() => setFaq([...faq, { question: "", answer: "" }])} data-testid="add-faq-button">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add
            </Button>
          </div>
          {faq.map((f, i) => (
            <div key={i} className="flex gap-2 mb-2">
              <Input placeholder="Question" value={f.question} onChange={(e) => setFaq(faq.map((x, idx) => idx === i ? { ...x, question: e.target.value } : x))} data-testid={`faq-question-${i}`} />
              <Input placeholder="Answer" value={f.answer} onChange={(e) => setFaq(faq.map((x, idx) => idx === i ? { ...x, answer: e.target.value } : x))} data-testid={`faq-answer-${i}`} />
              <button type="button" onClick={() => setFaq(faq.filter((_, idx) => idx !== i))} data-testid={`remove-faq-${i}`}><Trash2 className="w-4 h-4 text-slate-400 hover:text-red-600" /></button>
            </div>
          ))}
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-navy">References</label>
            <Button type="button" variant="outline" size="sm" onClick={() => setReferences([...references, { title: "", url: "", publisher: "" }])} data-testid="add-reference-button">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add
            </Button>
          </div>
          {references.map((r, i) => (
            <div key={i} className="flex gap-2 mb-2">
              <Input placeholder="Title" value={r.title} onChange={(e) => setReferences(references.map((x, idx) => idx === i ? { ...x, title: e.target.value } : x))} data-testid={`reference-title-${i}`} />
              <Input placeholder="URL" value={r.url} onChange={(e) => setReferences(references.map((x, idx) => idx === i ? { ...x, url: e.target.value } : x))} data-testid={`reference-url-${i}`} />
              <Input placeholder="Publisher" value={r.publisher} onChange={(e) => setReferences(references.map((x, idx) => idx === i ? { ...x, publisher: e.target.value } : x))} className="w-32" data-testid={`reference-publisher-${i}`} />
              <button type="button" onClick={() => setReferences(references.filter((_, idx) => idx !== i))} data-testid={`remove-reference-${i}`}><Trash2 className="w-4 h-4 text-slate-400 hover:text-red-600" /></button>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <Switch checked={form.featured} onCheckedChange={(v) => setForm((f) => ({ ...f, featured: v }))} data-testid="article-featured-switch" />
            <label className="text-sm">Featured</label>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={form.breaking} onCheckedChange={(v) => setForm((f) => ({ ...f, breaking: v }))} data-testid="article-breaking-switch" />
            <label className="text-sm">Breaking</label>
          </div>
          <Field label="Status" className="ml-auto w-40">
            <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
              <SelectTrigger data-testid="article-status-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="published">Published</SelectItem><SelectItem value="draft">Draft</SelectItem></SelectContent>
            </Select>
          </Field>
        </div>

        <Button onClick={handleSave} disabled={saving} data-testid="article-save-button" className="bg-gold hover:bg-gold-600 text-navy font-bold w-full">
          {saving ? "Saving..." : isEdit ? "Update Article" : "Create Article"}
        </Button>
      </div>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="text-sm font-medium text-navy mb-1 block">{label}</label>
      {children}
    </div>
  );
}
